<template>
  <div>
    <legend class="sr-only">
      {{ retrieveModalText('RequestDigitalSigningTitle') }}
    </legend>
    <div>
      <app-fieldset :title="retrieveModalText('RequestDigitalSigningTitle')">
        <form-text-area
          id="emailBody"
          v-model="emailBody"
          name="emailBody"
          title="Email body"
          rows="10"
          :show-label="false"
        />

        <form-input
          id="emailAddress"
          v-model="emailAddress"
          :title="retrieveModalText('EmailLabel')"
          type="text"
        >
        </form-input>

        <form-input
          id="phoneNumber"
          v-model="phoneNumber"
          :title="retrieveModalText('PhoneNumberLabel')"
          :error-message="phoneNumberErrorMessage"
          type="text"
        >
        </form-input>

        <form-checkbox id="isSignLocally" :title="retrieveModalText('SignLocallyLabel')">
          <input id="isSignLocally" v-model="isSignLocally" type="checkbox" name="isSignLocally" />
        </form-checkbox>

        <date-picker
          id="digitalSignExpireDate"
          :min-date="minDate"
          :date="digitalSignExpireDate"
          :title="retrieveModalText('ExpireDateLabel')"
          @change-date="handleDateChange"
        />
      </app-fieldset>
    </div>

    <app-button
      data-qa="digital-signing-form-button"
      :disabled="v$.$errors.length > 0"
      @click="submitDigitalSigning"
    >
      {{ i18n.t('/Common/Send') }}
    </app-button>
  </div>
</template>

<script>
import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import FormInput from '@/components/generic/presentational/atoms/formInput/FormInput.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import DatePicker from '@/components/generic/presentational/atoms/datePicker/DatePicker.vue'
import FormTextArea from '@/components/generic/presentational/atoms/formTextarea/FormTextarea.vue'
import FormCheckbox from '@/components/generic/presentational/atoms/formCheckbox/FormCheckbox.vue'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { useVuelidate } from '@vuelidate/core'
import RegexValidator from '@/helpers/RegexValidation'
import entityType from '@/helpers/enums/entityType'
import changeOrderService from '@/services/changeOrderService'
import quoteOrderService from '@/services/quoteOrderService'

export default {
  components: {
    AppButton,
    AppFieldset,
    FormInput,
    FormTextArea,
    FormCheckbox,
    DatePicker,
  },
  props: {
    entityType: {
      type: String,
      required: true,
    },
  },
  emits: ['submit-digital-signing'],
  setup() {
    return { v$: useVuelidate() }
  },
  validations() {
    const isValidPhone = (value) => {
      return RegexValidator(value, '^(\\+?[0-9]{10,12})$')
    }
    return {
      phoneNumber: {
        isValidPhone,
      },
    }
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      labelPrefix: '/QuoteActions/Forms/DigitalSigning/',
      emailBody: '',
      emailAddress: '',
      phoneNumber: '',
      isSignLocally: false,
      digitalSignExpireDate: null,
      minDate: moment(),
    }
  },
  computed: {
    ...mapGetters({
      currentQuote: 'quote/get',
      currentChangeOrder: 'changeOrder/get',
      quoteExpireDate: 'quote/expireDate',
    }),
    phoneNumberErrorMessage() {
      return this.v$.phoneNumber.$error
        ? 'Het telefoonnummer formaat moet 10 - 12 cijfers lang zijn'
        : ''
    },
    customerInfo() {
      return this.currentQuote.QuoteDetails.Customer
    },
  },
  async created() {
    if (this.entityType === entityType.changeOrder) {
      const response = await changeOrderService.getChangeOrderEmailInfoForDigitalSigning(
        this.currentChangeOrder.ChangeOrderId
      )

      this.emailBody = response.EmailBody
      this.emailAddress = response.EmailAddress
      this.phoneNumber = response.PhoneNumber?.replace(/[^+0-9]/g, '')
    } else {
      const response = await quoteOrderService.getQuoteEmailInfoForDigitalSigning(
        this.currentQuote.QuoteId
      )

      this.emailBody = response.EmailBody
      this.emailAddress = response.EmailAddress
      this.phoneNumber = response.PhoneNumber?.replace(/[^+0-9]/g, '')
      this.digitalSignExpireDate = this.quoteExpireDate ? moment.utc(this.quoteExpireDate) : null
    }

    this.$nextTick(() => {
      this.validateFields()
    })
  },
  methods: {
    validateFields() {
      this.v$.phoneNumber.$touch()
    },
    retrieveModalText(labelName) {
      return this.i18n.t(`${this.labelPrefix}${labelName}`) || labelName
    },
    handleDateChange(value) {
      if (value === 'INVALID') return

      this.digitalSignExpireDate = moment.utc(value)
    },
    async submitDigitalSigning() {
      this.validateFields()

      if (this.v$.$errors.length > 0) {
        return
      }

      let digitalSigningParams = {
        emailAddress: this.emailAddress,
        emailBody: this.emailBody,
        phoneNumber: this.phoneNumber,
        validUntilDate: moment.utc(this.digitalSignExpireDate).format(moment.HTML5_FMT.DATE),
        signLocally: this.isSignLocally,
      }
      this.$emit('submit-digital-signing', digitalSigningParams)
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(#digitalSignExpireDate-container) {
  .label {
    padding-right: 3rem;
  }
}
</style>
